<template>
  <v-container fluid class="analysis-group-detail">
    <v-skeleton-loader v-if="loading && !group" type="heading, chip@3, table-row@4" class="mt-2" />

    <v-alert v-else-if="error" type="error" outlined class="mt-2">
      <div class="d-flex align-center">
        <span>{{ error }}</span>
        <v-spacer />
        <v-btn small outlined color="error" @click="loadAll">
          <v-icon small left>mdi-refresh</v-icon>
          Retry
        </v-btn>
      </div>
    </v-alert>

    <template v-else-if="group">
      <!-- Header -->
      <v-card elevation="2" class="analysis-group-detail__header">
        <div class="analysis-group-detail__title-row">
          <div class="analysis-group-detail__title">
            <v-icon left color="primary">mdi-folder-multiple-outline</v-icon>
            <span class="headline">{{ group.name }}</span>
            <v-chip
              v-if="group.frozen_at"
              x-small
              outlined
              label
              class="ml-2"
              title="Configuration and engines are locked"
            >
              <v-icon x-small left>mdi-snowflake</v-icon>
              frozen
            </v-chip>
          </div>
          <v-spacer />
          <v-btn small outlined class="mr-2" @click="editDialog = true">
            <v-icon small left>mdi-pencil-outline</v-icon>
            Edit
          </v-btn>
          <v-btn small outlined class="mr-2 analysis-group-detail__add" @click="addDialog = true">
            <v-icon small left>mdi-video-plus-outline</v-icon>
            Add videos
          </v-btn>
          <v-btn
            small
            outlined
            class="mr-2"
            :disabled="!members.length"
            @click="snapshotDialog = true"
          >
            <v-icon small left>mdi-camera-plus-outline</v-icon>
            Save snapshot
          </v-btn>
          <v-btn
            small
            color="primary"
            depressed
            class="analysis-group-detail__run"
            :disabled="!members.length"
            @click="openRun()"
          >
            <v-icon small left>mdi-play</v-icon>
            Re-run group
          </v-btn>
          <v-menu offset-y>
            <template #activator="{ on, attrs }">
              <v-btn icon small class="ml-1" v-bind="attrs" v-on="on">
                <v-icon small>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item :to="`/coherence-evaluation?group=${group.id}`">
                <v-list-item-title>Open in evaluation</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="!group.frozen_at" @click="freeze">
                <v-list-item-title>Freeze</v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item @click="deleteDialog = { open: true, error: '', busy: false }">
                <v-list-item-title class="error--text">Delete group</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <div class="analysis-group-detail__chips">
          <v-chip x-small outlined label>{{ configLabel }}</v-chip>
          <v-chip v-for="engine in group.engines || []" :key="engine" x-small outlined label>
            {{ engine }}
          </v-chip>
          <v-chip v-if="group.threshold_pct != null" x-small outlined label>
            threshold {{ group.threshold_pct }}%
          </v-chip>
          <span class="caption grey--text ml-2">{{ summaryCaption }}</span>
        </div>
        <p v-if="group.description" class="body-2 grey--text text--darken-2 mb-0 mt-2">
          {{ group.description }}
        </p>
      </v-card>

      <!-- Tabs -->
      <v-tabs v-model="tab" class="analysis-group-detail__tabs">
        <v-tab href="#members" class="text-none">Members ({{ members.length }})</v-tab>
        <v-tab href="#runs" class="text-none">Runs ({{ runs.length }})</v-tab>
        <v-tab href="#metrics" class="text-none">Metrics</v-tab>
        <v-tab href="#series" class="text-none">Series ({{ snapshots.length }})</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab" class="analysis-group-detail__tab-items">
        <v-tab-item value="members">
          <div v-if="selectedMembers.length" class="analysis-group-detail__bulk">
            <span class="caption">{{ selectedMembers.length }} selected</span>
            <v-btn x-small outlined color="primary" class="ml-2" @click="openRun(selectedMembers)">
              <v-icon x-small left>mdi-play</v-icon>
              Re-run selected
            </v-btn>
            <v-btn x-small outlined color="error" class="ml-2" @click="removeSelected">
              <v-icon x-small left>mdi-close</v-icon>
              Remove selected
            </v-btn>
          </div>
          <GroupMembersTable
            :members="members"
            :runs="runs"
            :loading="loadingMembers"
            @pin="pinMember"
            @remove="removeMember"
            @add-members="addDialog = true"
            @selection-change="selectedMembers = $event"
          />
        </v-tab-item>

        <v-tab-item value="runs">
          <GroupRunsTable
            :runs="runs"
            :batch-summaries="batches"
            :pinned-run-ids="pinnedRunIds"
            :loading="loadingRuns"
            @cancel="cancelRun"
            @pin="pinFromRun"
            @run-group="openRun()"
          />
        </v-tab-item>

        <v-tab-item value="metrics">
          <v-skeleton-loader v-if="loadingMetrics && !metrics" type="card@2" />
          <v-alert v-else-if="metricsError" type="error" outlined dense class="mt-2">
            <div class="d-flex align-center">
              <span>{{ metricsError }}</span>
              <v-spacer />
              <v-btn small outlined color="error" @click="loadMetrics">Retry</v-btn>
            </div>
          </v-alert>
          <GroupMetricsPanel
            v-else-if="metrics"
            :global="metrics.global"
            :by-video="metrics.byVideo"
            :scope="metrics.scope"
            :applied-threshold="appliedThreshold"
            show-buckets
            show-run-column
            @apply-threshold="onApplyThreshold"
          />
        </v-tab-item>

        <v-tab-item value="series">
          <MetricsSeriesChart
            :snapshots="snapshots"
            :loading="loadingSnapshots"
            :saving="snapshotSaving"
            @save-snapshot="snapshotDialog = true"
          />
        </v-tab-item>
      </v-tabs-items>
    </template>

    <AnalysisGroupDialog
      v-model="editDialog"
      :group="group"
      :analysis-types="analysisTypes"
      :loading-analysis-types="loadingAnalysisTypes"
      :source-type="groupSourceType"
      @saved="onGroupSaved"
    />
    <AddToGroupDialog
      v-model="addDialog"
      :items="[]"
      :groups="group ? [group] : []"
      :locked-group-id="group ? group.id : null"
      @added="onMembersAdded"
    />
    <RunGroupDialog
      v-model="runDialog.open"
      :group="group"
      :member-ids="runDialog.memberIds"
      @launched="onLaunched"
    />
    <SaveSnapshotDialog
      v-model="snapshotDialog"
      :group-id="group ? group.id : null"
      :group-name="group ? group.name : ''"
      :summary="snapshotSummary"
      :saving="snapshotSaving"
      :error="snapshotError"
      @save="saveSnapshot"
    />

    <v-dialog v-model="deleteDialog.open" max-width="440">
      <v-card>
        <v-card-title class="px-6"><h5 class="primary-color mb-0">Delete group</h5></v-card-title>
        <v-card-text class="px-6">
          Delete <strong>{{ group ? group.name : '' }}</strong
          >? Runs and snapshots are kept.
          <v-alert v-if="deleteDialog.error" dense text type="error" class="mt-3 mb-0">
            {{ deleteDialog.error }}
          </v-alert>
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-6 py-3">
          <v-spacer />
          <v-btn
            text
            class="cancel-modal"
            :disabled="deleteDialog.busy"
            @click="deleteDialog.open = false"
          >
            Cancel
          </v-btn>
          <v-btn color="error" depressed :loading="deleteDialog.busy" @click="removeGroup"
            >Delete</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.visible" :timeout="6000" :color="snackbar.color" bottom>
      {{ snackbar.text }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.visible = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import GroupMetricsPanel, {
  normalizeMetricsPayload,
} from '@/components/evaluation/GroupMetricsPanel.vue';
import MetricsSeriesChart from '@/components/evaluation/MetricsSeriesChart.vue';
import SaveSnapshotDialog from '@/components/evaluation/SaveSnapshotDialog.vue';
import AddToGroupDialog from '@/components/groups/AddToGroupDialog.vue';
import AnalysisGroupDialog from '@/components/groups/AnalysisGroupDialog.vue';
import GroupMembersTable from '@/components/groups/GroupMembersTable.vue';
import GroupRunsTable from '@/components/groups/GroupRunsTable.vue';
import RunGroupDialog from '@/components/groups/RunGroupDialog.vue';
import AnalysisGroupsService from '@/services/AnalysisGroupsService';
import AnalysisRunsService from '@/services/AnalysisRunsService';
import InterviewsDataService from '@/services/InterviewsDataService';
import { summarizeResolutions } from '@/utils/groupResolution';

const LIVE_STATUSES = ['QUEUED', 'DISPATCHED', 'RUNNING', 'CANCEL_REQUESTED'];

function unwrap(response) {
  return response?.data?.data ?? response?.data ?? null;
}

function asList(body) {
  if (Array.isArray(body)) return body;
  if (body && Array.isArray(body.items)) return body.items;
  return [];
}

/**
 * /analysis-groups/:id (M4 screen): header with the group identity and actions, then the
 * Members (resolution, pin, remove), Runs (by batch), Metrics (GroupMetricsPanel over the group
 * scope) and Series (snapshots) tabs. Live changes arrive through the queue socket event and a
 * light poll while runs are alive.
 */
export default {
  name: 'AnalysisGroupDetail',

  components: {
    AddToGroupDialog,
    AnalysisGroupDialog,
    GroupMembersTable,
    GroupMetricsPanel,
    GroupRunsTable,
    MetricsSeriesChart,
    RunGroupDialog,
    SaveSnapshotDialog,
  },

  data() {
    return {
      group: null,
      members: [],
      runs: [],
      batches: [],
      snapshots: [],
      metrics: null,
      appliedThreshold: null,
      loading: false,
      loadingMembers: false,
      loadingRuns: false,
      loadingSnapshots: false,
      loadingMetrics: false,
      error: '',
      metricsError: '',
      tab: 'members',
      selectedMembers: [],
      analysisTypes: [],
      loadingAnalysisTypes: false,
      editDialog: false,
      addDialog: false,
      runDialog: { open: false, memberIds: [] },
      snapshotDialog: false,
      snapshotSaving: false,
      snapshotError: '',
      deleteDialog: { open: false, busy: false, error: '' },
      snackbar: { visible: false, text: '', color: 'success' },
      pollTimer: null,
    };
  },

  computed: {
    groupId() {
      return this.$route && this.$route.params ? this.$route.params.id : null;
    },
    configLabel() {
      return this.group && this.group.config_version
        ? `config v${this.group.config_version}`
        : 'config: current';
    },
    groupSourceType() {
      return this.members.some((member) => member.source_type === 'interview')
        ? 'interview'
        : 'manual_analysis';
    },
    resolutionSummary() {
      return summarizeResolutions(this.members);
    },
    summaryCaption() {
      const s = this.resolutionSummary;
      const parts = [`${s.total} ${s.total === 1 ? 'video' : 'videos'}`];
      if (s.evaluated) parts.push(`${s.evaluated} analysed`);
      if (s.pending) parts.push(`${s.pending} pending`);
      if (s.not_analysed - s.pending > 0) parts.push(`${s.not_analysed - s.pending} not analysed`);
      if (s.degraded) parts.push(`${s.degraded} degraded`);
      return parts.join(' · ');
    },
    pinnedRunIds() {
      return this.members.map((member) => member.pinned_run_id).filter(Boolean);
    },
    hasLiveRuns() {
      return this.runs.some((run) => LIVE_STATUSES.includes(String(run.status).toUpperCase()));
    },
    snapshotSummary() {
      if (!this.group) return '';
      const method = this.metrics?.global?.methods?.computed_subtraction || {};
      const fmt = (value) => (value === null || value === undefined ? '—' : `${value}%`);
      return `Group ${this.group.name} · ${this.members.length} videos · ${fmt(
        method.correctness_pct
      )} correct / ${fmt(method.truthful_flagged_pct)} false alarms`;
    },
  },

  watch: {
    groupId() {
      this.loadAll();
    },
    tab(value) {
      if (value === 'metrics' && !this.metrics && !this.loadingMetrics) this.loadMetrics();
      if (value === 'series' && !this.snapshots.length && !this.loadingSnapshots)
        this.loadSnapshots();
    },
    hasLiveRuns(live) {
      if (live) this.startPolling();
      else this.stopPolling();
    },
  },

  created() {
    if (this.$route && this.$route.query && this.$route.query.tab) this.tab = this.$route.query.tab;
    this.loadAll();
    this.loadAnalysisTypes();
    if (this.$socket && typeof this.$socket.on === 'function') {
      this.$socket.on('analysis-jobs-changed', this.onJobsChanged);
      this.$socket.on('analysis-group-snapshot-created', this.onSnapshotCreated);
    }
  },

  beforeDestroy() {
    this.stopPolling();
    if (this.$socket && typeof this.$socket.off === 'function') {
      this.$socket.off('analysis-jobs-changed', this.onJobsChanged);
      this.$socket.off('analysis-group-snapshot-created', this.onSnapshotCreated);
    }
  },

  methods: {
    notify(text, color = 'success') {
      this.snackbar = { visible: true, text, color };
    },

    errorText(error, fallback) {
      const data = error?.response?.data;
      return data?.message || data?.error || error?.message || fallback;
    },

    async loadAll() {
      if (!this.groupId) return;
      this.loading = true;
      this.error = '';
      try {
        const response = await AnalysisGroupsService.get(this.groupId);
        const body = unwrap(response) || {};
        this.group = body.group || body;
        if (Array.isArray(body.members)) this.members = body.members;
      } catch (error) {
        this.error =
          error?.response?.status === 404 ? 'Group not found.' : 'Failed to load the group.';
        this.loading = false;
        return;
      }
      this.loading = false;
      await Promise.all([this.loadMembers(), this.loadRuns()]);
      if (this.tab === 'metrics') this.loadMetrics();
      if (this.tab === 'series') this.loadSnapshots();
    },

    async loadMembers() {
      this.loadingMembers = true;
      try {
        this.members = asList(unwrap(await AnalysisGroupsService.members(this.groupId)));
      } catch (error) {
        this.notify('Could not load the members.', 'error');
      } finally {
        this.loadingMembers = false;
      }
    },

    async loadRuns() {
      this.loadingRuns = true;
      try {
        const [runsResponse, batchesResponse] = await Promise.all([
          AnalysisGroupsService.runs(this.groupId),
          AnalysisGroupsService.batches(this.groupId).catch(() => null),
        ]);
        this.runs = asList(unwrap(runsResponse));
        this.batches = batchesResponse ? asList(unwrap(batchesResponse)) : [];
      } catch (error) {
        this.notify('Could not load the runs.', 'error');
      } finally {
        this.loadingRuns = false;
      }
    },

    async loadMetrics() {
      this.loadingMetrics = true;
      this.metricsError = '';
      try {
        const params = this.appliedThreshold !== null ? { threshold: this.appliedThreshold } : {};
        const response = await AnalysisGroupsService.metrics(this.groupId, params);
        this.metrics = normalizeMetricsPayload(response.data);
      } catch (error) {
        this.metricsError = 'Failed to load the group metrics.';
      } finally {
        this.loadingMetrics = false;
      }
    },

    onApplyThreshold(value) {
      this.appliedThreshold = value;
      this.loadMetrics();
    },

    async loadSnapshots() {
      this.loadingSnapshots = true;
      try {
        this.snapshots = asList(unwrap(await AnalysisGroupsService.snapshots(this.groupId)));
      } catch (error) {
        this.snapshots = [];
      } finally {
        this.loadingSnapshots = false;
      }
    },

    async loadAnalysisTypes() {
      this.loadingAnalysisTypes = true;
      try {
        const items = (await InterviewsDataService.findAllInterviewAnalysisTypes(1, 100))?.data
          ?.data;
        this.analysisTypes = Array.isArray(items) ? items : [];
      } catch (error) {
        this.analysisTypes = [];
      } finally {
        this.loadingAnalysisTypes = false;
      }
    },

    startPolling() {
      if (this.pollTimer) return;
      this.pollTimer = setInterval(() => {
        this.loadRuns();
        this.loadMembers();
      }, 30000);
    },

    stopPolling() {
      if (this.pollTimer) clearInterval(this.pollTimer);
      this.pollTimer = null;
    },

    onJobsChanged(event) {
      if (!event || String(event.group_id ?? '') !== String(this.groupId)) return;
      this.loadRuns();
      if (['SUCCEEDED', 'FAILED', 'CANCELLED'].includes(String(event.status).toUpperCase())) {
        this.loadMembers();
        this.metrics = null;
      }
    },

    onSnapshotCreated(event) {
      if (!event || String(event.group_id ?? '') !== String(this.groupId)) return;
      this.notify(`Snapshot saved for batch ${event.batch || ''}`.trim(), 'info');
      this.loadSnapshots();
    },

    openRun(members = []) {
      this.runDialog = { open: true, memberIds: (members || []).map((m) => m.member_id ?? m.id) };
    },

    onGroupSaved(group) {
      if (group && group.id != null) this.group = { ...this.group, ...group };
      this.notify('Group saved');
      this.loadAll();
    },

    onMembersAdded(result) {
      const added = result.added != null ? `${result.added} added` : 'videos added';
      this.notify(result.skipped ? `${added} · ${result.skipped} already in the group` : added);
      this.loadMembers();
    },

    onLaunched(result) {
      const n = result.accepted.length;
      const parts = [`Queued ${n} ${n === 1 ? 'run' : 'runs'}`];
      if (result.batch) parts.push(`batch ${result.batch}`);
      if (result.skipped.length) parts.push(`${result.skipped.length} skipped`);
      this.notify(parts.join(' · '), n ? 'success' : 'warning');
      this.tab = 'runs';
      this.loadAll();
    },

    async pinMember({ member, run_id: runId }) {
      try {
        await AnalysisGroupsService.pinMember(this.groupId, member.member_id ?? member.id, runId);
        this.notify(runId ? 'Run pinned' : 'Run unpinned');
        this.loadMembers();
      } catch (error) {
        this.notify(this.errorText(error, 'Could not update the pin.'), 'error');
      }
    },

    pinFromRun(run) {
      const member = this.members.find(
        (m) => m.source_type === run.source_type && String(m.source_id) === String(run.source_id)
      );
      if (!member) return;
      this.pinMember({ member, run_id: run._id || run.run_id });
    },

    async removeMember(member) {
      try {
        await AnalysisGroupsService.removeMember(this.groupId, member.member_id ?? member.id);
        this.notify('Removed from group');
        this.loadMembers();
      } catch (error) {
        this.notify(this.errorText(error, 'Could not remove the video.'), 'error');
      }
    },

    async removeSelected() {
      const targets = [...this.selectedMembers];
      for (const member of targets) {
        try {
          await AnalysisGroupsService.removeMember(this.groupId, member.member_id ?? member.id);
        } catch (error) {
          this.notify(this.errorText(error, 'Could not remove a video.'), 'error');
        }
      }
      this.selectedMembers = [];
      this.loadMembers();
    },

    async cancelRun(run) {
      const id = run._id || run.run_id;
      try {
        if (String(run.status).toUpperCase() === 'QUEUED') await AnalysisRunsService.remove(id);
        else await AnalysisRunsService.cancel(id);
        this.notify('Cancellation requested', 'info');
        this.loadRuns();
      } catch (error) {
        this.notify(this.errorText(error, 'Could not cancel the run.'), 'error');
      }
    },

    async saveSnapshot({ note } = {}) {
      this.snapshotSaving = true;
      this.snapshotError = '';
      try {
        const payload = { note };
        if (this.appliedThreshold !== null) payload.threshold = this.appliedThreshold;
        await AnalysisGroupsService.createSnapshot(this.groupId, payload);
        this.snapshotDialog = false;
        this.notify('Snapshot saved');
        this.loadSnapshots();
      } catch (error) {
        this.snapshotError = this.errorText(error, 'Could not save the snapshot.');
      } finally {
        this.snapshotSaving = false;
      }
    },

    async freeze() {
      try {
        const body = unwrap(await AnalysisGroupsService.freeze(this.groupId));
        this.group = {
          ...this.group,
          ...(body || {}),
          frozen_at: body?.frozen_at || new Date().toISOString(),
        };
        this.notify('Group frozen');
      } catch (error) {
        this.notify(this.errorText(error, 'Could not freeze the group.'), 'error');
      }
    },

    async removeGroup() {
      this.deleteDialog.busy = true;
      this.deleteDialog.error = '';
      try {
        await AnalysisGroupsService.remove(this.groupId);
        this.deleteDialog.open = false;
        this.$router.push('/analysis-groups');
      } catch (error) {
        this.deleteDialog.error =
          error?.response?.status === 409
            ? 'The group still has queued or running runs. Cancel them or wait before deleting.'
            : this.errorText(error, 'Could not delete the group.');
      } finally {
        this.deleteDialog.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.analysis-group-detail {
  padding-top: 72px;
  max-width: 1400px;
}
.analysis-group-detail__header {
  padding: 16px 20px;
  border-radius: 10px !important;
  margin-bottom: 16px;
}
.analysis-group-detail__title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}
.analysis-group-detail__title {
  display: flex;
  align-items: center;
}
.analysis-group-detail__chips {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}
.analysis-group-detail__tabs {
  margin-bottom: 12px;
}
.analysis-group-detail__tab-items {
  background: transparent;
}
.analysis-group-detail__bulk {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}
</style>
